import MapMarkerIcon from '@assets/icons/map-marker.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_ZOOM,
  MAP_OPTIONS,
  PIN_MARKER_TRANSFORM,
} from '@/experiences/sales-portal/components/map/map-options.web';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '@usepepper/ui/molecules/map/loader-options';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import {
  LABEL_SHADOW,
  MAP_HEIGHT,
  MARKER_ICON_HEIGHT,
  MARKER_SIZE,
} from '@/experiences/sales-portal/components/map/constants';
import { MapProps } from '@/experiences/sales-portal/components/map/types';
import { MapSkeleton } from '@/experiences/sales-portal/components/map/map-skeleton';
import { Typography, View, YStack } from '@usepepper/sprout-kit-ui';

export const Map: React.FC<MapProps> = React.memo(({ coordinate, markers }) => {
  const [styles, theme] = useStyles();

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  if (!coordinate) {
    return null;
  }

  const center = {
    lat: coordinate.latitude,
    lng: coordinate.longitude,
  };

  if (!isLoaded || typeof google === 'undefined') {
    return <MapSkeleton />;
  }

  return (
    <GoogleMap
      center={center}
      mapContainerStyle={styles.mapContainer}
      options={MAP_OPTIONS}
      zoom={DEFAULT_ZOOM}
    >
      {markers?.map((marker) => (
        <OverlayView
          key={`map-marker-${marker.coordinate.latitude}:${marker.coordinate.longitude}`}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          position={{
            lat: marker.coordinate.latitude,
            lng: marker.coordinate.longitude,
          }}
        >
          <YStack
            accessibilityLabel={marker.accessibilityLabel ?? ''}
            alignItems="center"
            pointerEvents="none"
            transform={PIN_MARKER_TRANSFORM}
          >
            {marker.label ? (
              <View
                backgroundColor={theme.sk.color.bg.primary.default}
                borderRadius={theme.sk.radii.radiusMedium}
                marginBottom={theme.sk.spacing.spaceXsmall}
                overflow="hidden"
                padding={theme.sk.spacing.spaceSmall}
                style={LABEL_SHADOW}
              >
                <Typography numberOfLines={1} size="$xs" variant="label">
                  {marker.label}
                </Typography>
              </View>
            ) : null}
            <MapMarkerIcon
              fill={theme.sk.color.icon.primary}
              height={MARKER_ICON_HEIGHT}
              width={MARKER_SIZE}
            />
          </YStack>
        </OverlayView>
      ))}
    </GoogleMap>
  );
});

const useStyles = createStyles((theme) => ({
  mapContainer: {
    borderRadius: theme.sk.radii.radiusLarge,
    height: MAP_HEIGHT,
    overflow: 'hidden',
  },
}));
